import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import type { Msg } from "../db";
import * as S from "../state";
import { phoneOf, timeOf } from "../util";
import { Avatar } from "./Avatar";
import { sharing } from "./chatui";
import { EventDialog, PollDialog } from "./PollEvent";
import { ContactPicker } from "./Groups";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";

// ---- static map ---------------------------------------------------------------------

const TILE = 256;

/** A static map built from our own tile proxy (/map/tile/...), with a pin. */
export function MapView({ lat, lon, width, height, zoom = 15, live }: { lat: number; lon: number; width: number; height: number; zoom?: number; live?: boolean }) {
  const n = 2 ** zoom;
  const px = ((lon + 180) / 360) * n * TILE;
  const rad = (lat * Math.PI) / 180;
  const py = ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n * TILE;
  const left = px - width / 2;
  const top = py - height / 2;
  const tiles: { x: number; y: number; dx: number; dy: number }[] = [];
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + width) / TILE); tx++)
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty++) {
      if (ty < 0 || ty >= n) continue;
      tiles.push({ x: ((tx % n) + n) % n, y: ty, dx: tx * TILE - left, dy: ty * TILE - top });
    }
  return (
    <div class="map" style={{ width: `${width}px`, height: `${height}px` }}>
      {tiles.map((t) => (
        <img key={`${t.x}-${t.y}`} src={`/map/tile/${zoom}/${t.x}/${t.y}.png`} alt="" style={{ left: `${t.dx}px`, top: `${t.dy}px` }} loading="lazy" />
      ))}
      <span class={"map-pin" + (live ? " live" : "")} aria-hidden="true" />
      <small class="map-credit">© OpenStreetMap</small>
    </div>
  );
}

const osmLink = (lat: number, lon: number) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;

// ---- bubbles ---------------------------------------------------------------------------

export function LocationContent({ m }: { m: Msg }) {
  const loc = m.location!;
  const live = loc.live;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!live || live.stopped) return;
    const t = setInterval(() => tick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, [live?.stopped]);
  const active = !!live && !live.stopped && live.until > Date.now();
  const mine = m.dir === "out";
  return (
    <div class="location">
      <a href={osmLink(loc.lat, loc.lon)} target="_blank" rel="noopener noreferrer" aria-label="Open location in map">
        <MapView lat={loc.lat} lon={loc.lon} width={300} height={150} live={active} />
      </a>
      {live ? (
        <div class="live-row">
          <span class={"live-dot" + (active ? " on" : "")} />
          <div class="grow">
            <strong>{active ? "Live location" : "Live location ended"}</strong>
            <small class="muted">
              {active ? `Live until ${timeOf(live.until)}` : `Last updated ${timeOf(live.updatedAt || m.ts)}`}
              {active && live.updatedAt ? ` · updated ${timeOf(live.updatedAt)}` : ""}
            </small>
          </div>
          {mine && active && (
            <button class="stop-live" onClick={() => void engine.value?.stopLive(m.key)}>
              Stop sharing
            </button>
          )}
        </div>
      ) : (
        loc.name && <div class="loc-name">{loc.name}</div>
      )}
      <span class="coords" data-lat={loc.lat} data-lon={loc.lon}>
        {loc.lat.toFixed(5)}, {loc.lon.toFixed(5)}
        {loc.accuracy ? ` · ±${Math.round(loc.accuracy)} m` : ""}
      </span>
    </div>
  );
}

export function ContactContent({ m }: { m: Msg }) {
  const c = m.contact!;
  const onXmm = c.jid && c.jid !== S.myJid.value;
  const saved = c.jid && ["to", "both"].includes(S.roster.value.get(c.jid)?.subscription ?? "");
  return (
    <div class="contact-card">
      <div class="contact-card-top">
        <Avatar url={c.jid ? S.profiles.value.get(c.jid)?.avatar_url : null} jid={c.jid ?? c.phone} name={c.name} size={44} />
        <div class="grow">
          <strong>{c.name}</strong>
          <small class="muted">{c.phone}</small>
        </div>
      </div>
      <div class="contact-card-actions">
        {onXmm ? (
          <>
            <button onClick={() => void engine.value?.openChat(c.jid!)}>Message</button>
            {!saved && (
              <button onClick={() => void api.addContact(c.phone, c.name).then(() => toast(`${c.name} added to contacts`), (e) => toast(e.message))}>
                Add contact
              </button>
            )}
          </>
        ) : (
          <span class="muted small">Not on xmm</span>
        )}
      </div>
    </div>
  );
}

// ---- dialogs --------------------------------------------------------------------------

export function ShareDialogs({ jid }: { jid: string }) {
  if (sharing.value === "contact") return <ShareContact jid={jid} />;
  if (sharing.value === "location") return <ShareLocation jid={jid} />;
  if (sharing.value === "poll") return <PollDialog jid={jid} />;
  if (sharing.value === "event") return <EventDialog jid={jid} />;
  return null;
}

function ShareContact({ jid }: { jid: string }) {
  const [picked, setPicked] = useState<string[]>([]);
  const close = () => (sharing.value = null);
  async function send() {
    close();
    for (const c of picked) {
      await engine.value?.sendDraft(jid, { body: "", kind: "contact", contact: { name: S.displayName(c), phone: phoneOf(c), jid: c } });
    }
  }
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal forward" role="dialog" aria-label="Share contacts" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Contacts to send</h2>
        </header>
        <ContactPicker picked={picked} onChange={setPicked} />
        {picked.length > 0 && (
          <footer class="forward-footer">
            <span class="ellipsis">{picked.map((j) => S.displayName(j)).join(", ")}</span>
            <button class="icon-btn send-fab" onClick={() => void send()} aria-label="Send contacts">
              <Icon name="send" />
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

const LIVE_OPTIONS = [
  { label: "15 minutes", ms: 15 * 60_000 },
  { label: "1 hour", ms: 3_600_000 },
  { label: "8 hours", ms: 8 * 3_600_000 },
];

function ShareLocation({ jid }: { jid: string }) {
  const [pos, setPos] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState("");
  const [liveFor, setLiveFor] = useState(LIVE_OPTIONS[0].ms);
  const close = () => (sharing.value = null);

  useEffect(() => {
    if (!navigator.geolocation) return setError("Location is not available in this browser.");
    const id = navigator.geolocation.watchPosition(setPos, (e) => setError(e.code === e.PERMISSION_DENIED ? "Allow location access to share your location." : e.message), {
      enableHighAccuracy: true,
      timeout: 15_000,
    });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  async function send(live: boolean) {
    if (!pos) return;
    close();
    const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) };
    await engine.value?.sendDraft(jid, {
      body: "",
      kind: "location",
      location: live ? { ...loc, live: { until: Date.now() + liveFor, updatedAt: Date.now() } } : loc,
    });
  }

  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal forward share-location" role="dialog" aria-label="Send location" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Send location</h2>
        </header>
        {error ? (
          <p class="error pad">{error}</p>
        ) : !pos ? (
          <p class="muted pad">Finding your location…</p>
        ) : (
          <>
            <MapView lat={pos.coords.latitude} lon={pos.coords.longitude} width={440} height={220} zoom={16} />
            <button class="list-action" onClick={() => void send(false)}>
              <span class="list-action-icon">
                <Icon name="pin" />
              </span>
              <div>
                <div>Send your current location</div>
                <small class="muted">Accurate to {Math.round(pos.coords.accuracy)} metres</small>
              </div>
            </button>
            <div class="live-share">
              <button class="list-action" onClick={() => void send(true)}>
                <span class="list-action-icon live">
                  <Icon name="pin" />
                </span>
                <div>
                  <div>Share live location</div>
                  <small class="muted">Updates as you move, while this tab is open</small>
                </div>
              </button>
              <select value={liveFor} onChange={(e) => setLiveFor(Number(e.currentTarget.value))} aria-label="Share live location for">
                {LIVE_OPTIONS.map((o) => (
                  <option key={o.ms} value={o.ms}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
